import { type MissionState } from '../../../modules/missions/types/state/missions';
import { type MissionOperationType, type MissionFormatRule } from '../../../modules/missions/types/state/missions';
import { type MissionReward } from '../../../modules/missions/types/state/missions';
import { type StateType } from '../../../states';
import { type EntitiesMissions } from '../../../states/entities/missions';
import { type Selector } from '../../../types/selectors';
import { getMissionIdFromProps } from '../../_propsGetters_';
import { selectEntitiesState } from '../entitiesPrimary.selector';

const emptyObject = {};
const emptyArray: [] = [];
const emptyStringArray: Array<string> = [];
const emptyFormatRuleArray: Array<MissionFormatRule> = [];

export const selectEntitiesMissions: Selector<EntitiesMissions> = (state: StateType) =>
  selectEntitiesState(state)?.missions ?? emptyObject;

type EntitiesMissionsSelectorProps = {
  missionId?: number;
};

export const selectEntitiesMissionsEntity: Selector<MissionState | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => {
  const missionId = getMissionIdFromProps(state, props);

  if (missionId) {
    return selectEntitiesMissions(state)[missionId];
  }
};

export const selectEntitiesMissionsEntityMissionId: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.missionId;

export const selectEntitiesMissionsEntityStartDate: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.startDate;

export const selectEntitiesMissionsEntityEndDate: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.endDate;

export const selectEntitiesMissionsEntityImageUrl: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.imageUrl;

export const selectEntitiesMissionsEntityLogoUrl: Selector<string | null | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.logoUrl;

export const selectEntitiesMissionsEntityOperationType: Selector<MissionOperationType | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.operationType;

export const selectEntitiesMissionsEntityOptInEnabled: Selector<boolean> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.optInEnabled ?? false;

export const selectEntitiesMissionsEntityShowTimer: Selector<boolean> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.showTimer ?? false;

export const selectEntitiesMissionsEntityMinPlacedAmount: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.minPlacedAmount;

export const selectEntitiesMissionsEntityMinBetOdds: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.minBetOdds;

export const selectEntitiesMissionsEntityAchievementIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.achievementIds ?? emptyArray;

export const selectEntitiesMissionsEntityTitle: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.title;

export const selectEntitiesMissionsEntityDescription: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.description;

export const selectEntitiesMissionsEntityRedirectUrl: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.redirectUrl;

export const selectEntitiesMissionsEntityRulesUrl: Selector<string> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.rulesUrl ?? '';

export const selectEntitiesMissionsEntityReward: Selector<MissionReward | null | undefined> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.reward;

export const selectEntitiesMissionsEntityColors: Selector<Array<string>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.colors ?? emptyStringArray;

export const selectEntitiesMissionsEntityRules: Selector<Array<string>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.rules ?? emptyStringArray;

export const selectEntitiesMissionsEntityFormatRules: Selector<Array<MissionFormatRule>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.formatRules ?? emptyFormatRuleArray;

export const selectEntitiesMissionsEntitySportIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.sportIds ?? emptyArray;

export const selectEntitiesMissionsEntityCategoryIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.categoryIds ?? emptyArray;

export const selectEntitiesMissionsEntityTournamentIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.tournamentIds ?? emptyArray;

export const selectEntitiesMissionsEntityMatchIds: Selector<Array<number>> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.matchIds ?? emptyArray;

export const selectEntitiesMissionsEntityIsArchived: Selector<boolean> = (
  state: StateType,
  props: EntitiesMissionsSelectorProps = {},
) => selectEntitiesMissionsEntity(state, props)?.isArchived ?? false;
